// useAutoRefresh — runs `callback` on a periodic timer while the
// tab is visible, and re-runs immediately when the tab regains
// focus after being hidden.
//
// Why this exists: the email surface fetches the message list and
// the folder unread counts ONCE on mount. Without something like
// this hook, a new email arrives and the user sees nothing change
// in Stewardship OS until they manually click "Refresh" or
// navigate away and back. Grant hit this 2026-05-04 — Outlook
// showed a new Schwab statement, the OS sidebar's unread badge
// updated (it had been re-fetched by a separate route nav), but
// the inbox message list still didn't include the new email.
//
// Behavior:
//   - Fires `callback` every `intervalMs` while the tab is visible.
//   - When the tab is hidden (Page Visibility API), the timer is
//     suspended — saves Worker invocations + Graph quota.
//   - When the tab regains focus after being hidden for >= 5s,
//     fires `callback` immediately so the user sees fresh data
//     within seconds of returning, not at the next interval tick.
//   - Cleans up timers + listeners on unmount.
//
// JSX-free so the hook can be unit-tested without a JSX-aware
// tsconfig — matches the pattern established for the email
// helpers earlier in this phase.

import { useEffect, useRef } from "react";

export interface UseAutoRefreshOptions {
  // Milliseconds between background refreshes while the tab is
  // visible. Longer is gentler on Microsoft's quota; shorter
  // feels snappier. 60_000-90_000 is the sweet spot for inbox
  // surfaces.
  intervalMs: number;
  // When the tab regains focus after being hidden for at least
  // this many milliseconds, fire `callback` immediately. Default
  // 5_000 — short enough to feel responsive, long enough to
  // ignore micro-blurs (alt-tab to a popup that returns instantly).
  visibilityRefocusMinHiddenMs?: number;
  // Set to true to disable the auto-refresh entirely (e.g., a
  // route is loading data via another mechanism). The hook still
  // mounts but does nothing.
  paused?: boolean;
}

export function useAutoRefresh(
  callback: () => void,
  options: UseAutoRefreshOptions,
): void {
  const {
    intervalMs,
    visibilityRefocusMinHiddenMs = 5_000,
    paused = false,
  } = options;

  // Stash the latest callback in a ref so the effect below doesn't
  // re-subscribe every time the parent re-renders. The interval +
  // listener stay stable for the lifetime of the component;
  // they always invoke the freshest callback.
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    if (paused) return;
    if (intervalMs <= 0) return;

    let hiddenAt: number | null = null;

    const fire = () => {
      try {
        callbackRef.current();
      } catch {
        // Swallow — a refresh callback that throws shouldn't
        // tear down the whole hook. The caller's own error
        // handling (toast / inline banner) is the right place
        // for surface UX.
      }
    };

    const onVisibilityChange = () => {
      const doc = (globalThis as unknown as { document?: Document }).document;
      if (!doc) return;
      if (doc.visibilityState === "hidden") {
        hiddenAt = Date.now();
        return;
      }
      // Tab is now visible. Decide whether to refresh now.
      if (hiddenAt !== null) {
        const hiddenForMs = Date.now() - hiddenAt;
        hiddenAt = null;
        if (hiddenForMs >= visibilityRefocusMinHiddenMs) {
          fire();
        }
      }
    };

    const intervalId = setInterval(() => {
      const doc = (globalThis as unknown as { document?: Document }).document;
      // Skip ticks while the tab is hidden — they're noisy + waste
      // Worker invocations.
      if (doc && doc.visibilityState === "hidden") return;
      fire();
    }, intervalMs);

    const doc = (globalThis as unknown as { document?: Document }).document;
    doc?.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      clearInterval(intervalId);
      doc?.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [intervalMs, visibilityRefocusMinHiddenMs, paused]);
}
